import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../lib/content";

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const articles = t("data.articles", { returnObjects: true });
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const heroSrc = IMAGES[article.heroImg || article.img];

  return (
    <>
      <section
        data-testid="blog-post-hero"
        className="relative bg-paper border-b border-slate-200"
      >
        <div className="container-mtbl pt-14 md:pt-20">
          <Link
            to="/insights"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-[#0B1A3D] text-sm font-semibold hover:opacity-70 transition-opacity"
          >
            <ArrowLeft size={16} /> {t("common.allPerspectives")}
          </Link>

          <div className="mt-10 max-w-4xl">
            <div className="flex items-center gap-4 text-xs tracking-[0.2em] uppercase text-slate-500">
              <span>{article.category}</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>{article.date}</span>
            </div>
            <h1
              data-testid="blog-post-title"
              className="mt-6 text-[#0B1A3D] text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-tighter leading-[1.05]"
            >
              {article.title}
            </h1>
            <p className="mt-7 text-slate-600 text-lg md:text-xl leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </div>

        <div className="container-mtbl mt-14 pb-0">
          <div className="relative aspect-[16/8] md:aspect-[16/7] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(11,26,61,0.12)]">
            <img
              src={heroSrc}
              alt={article.title}
              data-testid="blog-post-hero-image"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="blog-post-body">
        <div className="container-mtbl">
          <article className="max-w-3xl mx-auto">
            {article.sections?.map((s, idx) => {
              if (s.kind === "heading") {
                return (
                  <h2
                    key={idx}
                    className="mt-14 mb-5 text-[#0B1A3D] text-2xl md:text-3xl font-semibold tracking-tight leading-tight"
                  >
                    {s.text}
                  </h2>
                );
              }
              if (s.kind === "paragraph") {
                return (
                  <p
                    key={idx}
                    className="mt-6 text-slate-700 text-base md:text-lg leading-[1.8]"
                  >
                    {s.text}
                  </p>
                );
              }
              if (s.kind === "list") {
                return (
                  <ul key={idx} className="mt-6 space-y-3">
                    {s.items.map((it, j) => (
                      <li
                        key={j}
                        className="flex gap-4 text-slate-700 text-base md:text-lg leading-[1.7]"
                      >
                        <span className="mt-3 h-1.5 w-1.5 rounded-full bg-[#0B1A3D] shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (s.kind === "image") {
                return (
                  <figure key={idx} className="my-14">
                    <div className="relative aspect-[16/10] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(11,26,61,0.1)]">
                      <img
                        src={IMAGES[s.src]}
                        alt={s.caption || article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {s.caption && (
                      <figcaption className="mt-4 text-slate-500 text-sm italic leading-relaxed text-center">
                        {s.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              return null;
            })}

            <div className="mt-16 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/contact?type=strategic"
                data-testid="blog-post-contact-cta"
                className="btn-primary"
              >
                {t("common.engageWithMtbl")}
              </Link>
              <Link
                to="/insights"
                data-testid="blog-post-back-cta"
                className="inline-flex items-center gap-2 text-[#0B1A3D] text-sm font-semibold hover:opacity-70"
              >
                <ArrowLeft size={16} /> {t("common.backToInsights")}
              </Link>
            </div>
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section
          className="section-pad bg-paper border-t border-slate-200"
          data-testid="blog-post-related"
        >
          <div className="container-mtbl">
            <p className="overline mb-5">{t("insights.relatedOverline")}</p>
            <h3 className="text-[#0B1A3D] text-2xl md:text-3xl font-semibold tracking-tight">
              {t("insights.relatedTitle")}
            </h3>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a, i) => {
                const to = a.slug ? `/insights/${a.slug}` : "/insights";
                return (
                  <Link
                    to={to}
                    key={a.title}
                    data-testid={`related-card-${i}`}
                    className="group card-soft overflow-hidden block"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={IMAGES[a.img]}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-7">
                      <div className="flex items-center justify-between text-xs tracking-wide uppercase">
                        <span className="text-slate-500">{a.category}</span>
                        <span className="text-slate-400">{a.date}</span>
                      </div>
                      <h4 className="mt-4 text-[#0B1A3D] text-lg font-semibold tracking-tight leading-snug">
                        {a.title}
                      </h4>
                      <div className="mt-5 inline-flex items-center gap-2 text-[#0B1A3D] text-sm font-semibold">
                        {t("common.readMore")} <ArrowRight size={16} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
